import { useEffect, useMemo, useState } from "react";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Users, Zap, CreditCard, Sparkles, ExternalLink, AlertCircle, CheckCircle2, Mail, ChevronDown, ChevronRight, Trash2, Filter, FlaskConical } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

// Heuristics to detect testing-agent / manual test accounts.
// A user is considered "test" when its email matches known test domains OR local-part prefixes.
const TEST_EMAIL_DOMAINS = ["vxftest.example.com", "vxfsample.com", "example.com", "example.org", "example.net"];
const TEST_EMAIL_PREFIXES = ["e2e_", "otheruser_", "uitest", "bugtest", "portaltst", "uiuser", "deletetest_", "ratelimit_", "flow_", "reglimit_", "seed_", "smoke_", "qa_"];
function isTestEmail(email = "") {
  const e = (email || "").toLowerCase();
  if (!e.includes("@")) return false;
  const [local, domain] = e.split("@");
  if (TEST_EMAIL_DOMAINS.some((d) => domain === d || domain.endsWith("." + d))) return true;
  if (TEST_EMAIL_PREFIXES.some((p) => local.startsWith(p))) return true;
  return false;
}

export default function Admin() {
  const { t, lang } = useI18n();
  const { user: currentUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [config, setConfig] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [ticketCounts, setTicketCounts] = useState({ open: 0, closed: 0, total: 0 });
  const [ticketFilter, setTicketFilter] = useState("open");
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [showTestOnly, setShowTestOnly] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState("");
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0, errors: 0 });

  const loadTickets = (status = ticketFilter) => {
    api.get(`/admin/support/tickets?status=${status}`).then((r) => {
      setTickets(r.data.tickets || []);
      setTicketCounts(r.data.counts || { open: 0, closed: 0, total: 0 });
    });
  };

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/config").then((r) => setConfig(r.data));
    loadTickets("open");
  }, []);

  const changeFilter = (f) => { setTicketFilter(f); loadTickets(f); };
  const toggleTicket = (id) => setExpandedTicket((c) => (c === id ? null : id));
  const updateTicketStatus = async (ticket_id, status) => {
    await api.patch(`/admin/support/tickets/${ticket_id}`, { status });
    loadTickets();
  };

  const askDelete = (u) => { setUserToDelete(u); setConfirmText(""); };
  const closeDelete = () => { setUserToDelete(null); setConfirmText(""); };
  const doDelete = async () => {
    if (!userToDelete) return;
    if (confirmText.trim().toLowerCase() !== (userToDelete.email || "").toLowerCase()) {
      toast.error(t("admin_delete_email_mismatch") || "Type the exact email to confirm.");
      return;
    }
    setDeleting(true);
    try {
      const { data } = await api.delete(`/admin/users/${userToDelete.user_id}`);
      const summary = Object.entries(data.deleted || {}).filter(([, n]) => n > 0).map(([k, n]) => `${k}: ${n}`).join(" · ");
      toast.success((t("admin_deleted_ok") || "User deleted") + (summary ? ` — ${summary}` : ""));
      if (data.had_stripe_customer) {
        toast.warning(t("admin_delete_stripe_warn") || "This user had a Stripe customer. If they had an active subscription, cancel it manually from the Stripe dashboard.");
      }
      // Refresh lists & stats
      api.get("/admin/stats").then((r) => setStats(r.data));
      api.get("/admin/users").then((r) => setUsers(r.data));
      closeDelete();
    } catch (err) {
      toast.error(errorMessage(err, t));
    } finally {
      setDeleting(false);
    }
  };

  // Test-user filter + bulk delete
  const deletableTestUsers = useMemo(() => users.filter((u) => {
    const isSelf = currentUser && currentUser.user_id === u.user_id;
    return !isSelf && u.role !== "admin" && isTestEmail(u.email);
  }), [users, currentUser]);
  const displayedUsers = useMemo(() => showTestOnly ? users.filter((u) => isTestEmail(u.email)) : users, [users, showTestOnly]);

  const runBulkDelete = async () => {
    if (bulkConfirm.trim() !== `DELETE ${deletableTestUsers.length}`) {
      toast.error(t("admin_bulk_mismatch") || "Confirmation phrase doesn't match.");
      return;
    }
    setBulkRunning(true);
    setBulkProgress({ done: 0, total: deletableTestUsers.length, errors: 0 });
    let done = 0, errors = 0;
    for (const u of deletableTestUsers) {
      try {
        await api.delete(`/admin/users/${u.user_id}`);
      } catch { errors += 1; }
      done += 1;
      setBulkProgress({ done, total: deletableTestUsers.length, errors });
    }
    toast.success(`${done - errors}/${deletableTestUsers.length} ${t("admin_bulk_done") || "test users deleted"}${errors ? ` · ${errors} ${t("admin_bulk_errors") || "errors"}` : ""}`);
    // Refresh
    const [sr, ur] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users")]);
    setStats(sr.data);
    setUsers(ur.data);
    setBulkRunning(false);
    setBulkOpen(false);
    setBulkConfirm("");
  };

  if (!stats) return null;

  const isTestMode = config?.stripe_mode === "test";
  const onboardingUrl = config?.stripe_onboarding_url;

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="text-[10px] uppercase tracking-[0.2em] text-orange-400 font-bold mb-2">{t("admin")}</div>
      <h1 className="font-black text-4xl md:text-5xl tracking-tight mb-8">{t("admin_dash")}</h1>

      {/* Payments setup / Stripe status */}
      <div className={`rounded-md border p-6 mb-10 ${isTestMode ? "border-amber-500/40 bg-amber-500/[0.06]" : "border-emerald-500/40 bg-emerald-500/[0.05]"}`} data-testid="stripe-status-card">
        <div className="flex items-start gap-4 flex-wrap">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isTestMode ? "bg-amber-500/20" : "bg-emerald-500/20"}`}>
            {isTestMode ? <AlertCircle className="w-5 h-5 text-amber-400" /> : <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
          </div>
          <div className="flex-1 min-w-[280px]">
            <div className={`text-[10px] uppercase tracking-[0.2em] font-bold mb-1 ${isTestMode ? "text-amber-400" : "text-emerald-400"}`}>
              Stripe · {isTestMode ? "Test mode" : "Live"}
            </div>
            <h3 className="font-black text-xl tracking-tight mb-2">
              {isTestMode ? "Claim your Stripe account to receive real payments" : "Live payments active"}
            </h3>
            {isTestMode ? (
              <>
                <p className="text-sm text-zinc-300 leading-relaxed mb-4">
                  You are currently in <strong>test mode</strong>. Cards on checkout (4242…) go to a virtual test balance, no real money moves.
                  To start receiving real payments — and to unlock <strong>PayPal, Apple Pay, Google Pay</strong> in the checkout — claim your Stripe sandbox and complete onboarding.
                </p>
                <div className="grid md:grid-cols-2 gap-3 mb-4 text-xs text-zinc-400">
                  <div className="rounded border border-white/5 bg-[#0E0E12]/20 p-3">
                    <div className="font-bold text-white mb-1">Where does the money go?</div>
                    <div>Every card / Apple Pay / PayPal payment lands in your <strong>Stripe balance</strong>. Stripe then pays out to your bank account (IBAN) every 2–7 days.</div>
                  </div>
                  <div className="rounded border border-white/5 bg-[#0E0E12]/20 p-3">
                    <div className="font-bold text-white mb-1">Enable more payment methods</div>
                    <div>After onboarding: <strong>Dashboard → Settings → Payment methods</strong> and turn on PayPal, Apple Pay, Google Pay, Klarna, SEPA — they will appear automatically on checkout.</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {onboardingUrl && (
                    <a href={onboardingUrl} target="_blank" rel="noreferrer" data-testid="stripe-onboarding-link">
                      <button className="rounded-full bg-orange-500 hover:bg-orange-600 text-black font-bold text-sm px-5 h-10 inline-flex items-center gap-2">
                        Claim Stripe account <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                    </a>
                  )}
                  <a href="https://dashboard.stripe.com" target="_blank" rel="noreferrer" data-testid="stripe-dashboard-link">
                    <button className="rounded-full bg-white/10 hover:bg-white/15 border border-white/10 text-white font-semibold text-sm px-5 h-10 inline-flex items-center gap-2">
                      Open Stripe dashboard <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                  </a>
                  <a href="https://dashboard.stripe.com/settings/payment_methods" target="_blank" rel="noreferrer" data-testid="stripe-methods-link">
                    <button className="rounded-full bg-white/10 hover:bg-white/15 border border-white/10 text-white font-semibold text-sm px-5 h-10 inline-flex items-center gap-2">
                      Enable PayPal & more <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                  </a>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-zinc-300 leading-relaxed mb-4">
                  Real payments are on. Every card, Apple Pay, Google Pay or PayPal charge on Vixelforge lands in your <strong>Stripe balance</strong> and is paid out to your bank account (IBAN) every 2–7 days.
                </p>
                <div className="flex flex-wrap gap-2">
                  <a href="https://dashboard.stripe.com" target="_blank" rel="noreferrer" data-testid="stripe-dashboard-link">
                    <button className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-bold text-sm px-5 h-10 inline-flex items-center gap-2">
                      Open Stripe dashboard <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                  </a>
                  <a href="https://dashboard.stripe.com/payments" target="_blank" rel="noreferrer" data-testid="stripe-payments-link">
                    <button className="rounded-full bg-white/10 hover:bg-white/15 border border-white/10 text-white font-semibold text-sm px-5 h-10 inline-flex items-center gap-2">
                      View payments <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                  </a>
                  <a href="https://dashboard.stripe.com/settings/payment_methods" target="_blank" rel="noreferrer" data-testid="stripe-methods-link">
                    <button className="rounded-full bg-white/10 hover:bg-white/15 border border-white/10 text-white font-semibold text-sm px-5 h-10 inline-flex items-center gap-2">
                      Payment methods <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-4 mb-10">
        {[
          { icon: Users, label: t("total_users"), value: stats.total_users },
          { icon: Sparkles, label: t("prompts_generated"), value: stats.total_prompts_generated },
          { icon: CreditCard, label: t("payments"), value: stats.paid_transactions },
          { icon: Zap, label: t("active_plans"), value: ((stats.plans.basic || 0) + (stats.plans.plus || 0) + (stats.plans.pro || 0)) },
        ].map((s, i) => (
          <div key={i} className="rounded-md border border-white/10 bg-white/[0.02] p-5" data-testid={`admin-stat-${i}`}>
            <s.icon className="w-5 h-5 text-orange-400 mb-3" />
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{s.label}</div>
            <div className="font-black text-3xl mt-1">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="rounded-md border border-white/10 bg-white/[0.02] p-4 mb-8">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-3">{t("plan_dist")}</div>
        <div className="grid grid-cols-4 gap-4">
          {["pending","basic","plus","pro"].map((k) => (
            <div key={k} className="text-center">
              <div className="text-2xl font-black">{stats.plans[k] || 0}</div>
              <div className="text-xs uppercase tracking-widest text-orange-400 font-bold">{k}</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
          <h2 className="font-black text-2xl tracking-tight">{t("users")}</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setShowTestOnly((v) => !v)}
              data-testid="admin-toggle-test-only"
              className={`inline-flex items-center gap-2 h-9 rounded-full px-3 text-xs uppercase tracking-widest font-bold transition-colors ${showTestOnly ? "bg-orange-500 text-black" : "bg-white/5 hover:bg-white/10 text-zinc-300"}`}
              title={t("admin_filter_test_hint") || "Match @example.com / @vxftest.example.com and known test prefixes"}
            >
              <FlaskConical className="w-3.5 h-3.5" />
              {t("admin_filter_test") || "Show test users only"}
              {showTestOnly && <span className="rounded bg-[#0E0E12]/20 px-1.5 py-0.5">{displayedUsers.length}</span>}
            </button>
            <Button
              onClick={() => { setBulkConfirm(""); setBulkOpen(true); }}
              disabled={deletableTestUsers.length === 0}
              data-testid="admin-bulk-delete-test"
              className="h-9 bg-rose-500 hover:bg-rose-600 text-white font-bold text-xs uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-3.5 h-3.5 mr-2" />
              {t("admin_bulk_delete_test") || "Delete all test users"} · {deletableTestUsers.length}
            </Button>
          </div>
        </div>
        <div className="rounded-md border border-white/10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
              <tr>
                <th className="text-left px-4 py-3">{t("name")}</th>
                <th className="text-left px-4 py-3">{t("email")}</th>
                <th className="text-left px-4 py-3">{t("current_plan")}</th>
                <th className="text-left px-4 py-3">{t("used")}</th>
                <th className="text-left px-4 py-3">{t("joined")}</th>
                <th className="text-right px-4 py-3">{t("actions") || "Actions"}</th>
              </tr>
            </thead>
            <tbody>
              {displayedUsers.map((u) => {
                const isSelf = currentUser && currentUser.user_id === u.user_id;
                const isAdmin = u.role === "admin";
                const isTest = isTestEmail(u.email);
                const canDelete = !isSelf && !isAdmin;
                return (
                  <tr key={u.user_id} className="border-t border-white/5" data-testid={`admin-user-${u.user_id}`}>
                    <td className="px-4 py-3">{u.name}</td>
                    <td className="px-4 py-3 text-zinc-400">
                      <span className="inline-flex items-center gap-2">
                        {u.email}
                        {isTest && !isAdmin && <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 font-bold" title={t("admin_test_badge_hint") || "Detected as a test account"}>test</span>}
                      </span>
                    </td>
                    <td className="px-4 py-3"><span className="text-[10px] uppercase tracking-widest text-orange-400 font-bold">{u.plan}</span>{isAdmin && <span className="ml-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 font-bold">Admin</span>}</td>
                    <td className="px-4 py-3">{u.monthly_prompts_used || 0}</td>
                    <td className="px-4 py-3 text-zinc-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString(lang) : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => askDelete(u)}
                        disabled={!canDelete}
                        data-testid={`admin-delete-user-${u.user_id}`}
                        title={isSelf ? (t("admin_delete_self_disabled") || "You cannot delete your own account") : isAdmin ? (t("admin_delete_admin_disabled") || "Cannot delete an admin") : (t("admin_delete_user") || "Delete user")}
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors ${canDelete ? "text-zinc-400 hover:text-rose-400 hover:bg-rose-500/10" : "text-zinc-700 cursor-not-allowed"}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {displayedUsers.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-500 text-sm">{t("admin_no_test_users") || "No test users found."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Support tickets */}
      <div className="mt-12">
        <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
          <h2 className="font-black text-2xl tracking-tight inline-flex items-center gap-2">
            <Mail className="w-5 h-5 text-orange-400" />
            {t("admin_tickets")}
          </h2>
          <div className="flex gap-2 text-xs">
            {[
              { k: "open", label: t("admin_tickets_open"), count: ticketCounts.open },
              { k: "closed", label: t("admin_tickets_closed"), count: ticketCounts.closed },
              { k: "all", label: t("admin_tickets_all"), count: ticketCounts.total },
            ].map((f) => (
              <button
                key={f.k}
                onClick={() => changeFilter(f.k)}
                data-testid={`ticket-filter-${f.k}`}
                className={`rounded-full px-3 h-8 uppercase tracking-widest font-bold transition-colors ${ticketFilter === f.k ? "bg-orange-500 text-black" : "bg-white/5 hover:bg-white/10 text-zinc-300"}`}
              >
                {f.label} · {f.count}
              </button>
            ))}
          </div>
        </div>

        {tickets.length === 0 ? (
          <div className="rounded-md border border-white/10 bg-white/[0.02] px-6 py-10 text-center text-sm text-zinc-400" data-testid="tickets-empty">
            {t("admin_tickets_empty")}
          </div>
        ) : (
          <div className="rounded-md border border-white/10 overflow-hidden divide-y divide-white/5">
            {tickets.map((tk) => {
              const isOpen = expandedTicket === tk.ticket_id;
              const isClosed = tk.status === "closed";
              return (
                <div key={tk.ticket_id} className={isClosed ? "opacity-60" : ""} data-testid={`ticket-${tk.ticket_id}`}>
                  <button
                    onClick={() => toggleTicket(tk.ticket_id)}
                    className="w-full flex items-start gap-4 px-5 py-4 text-left hover:bg-white/[0.02] transition-colors"
                  >
                    <div className="mt-1">
                      {isOpen ? <ChevronDown className="w-4 h-4 text-orange-400" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`text-[10px] uppercase tracking-widest font-bold ${isClosed ? "text-zinc-500" : "text-emerald-400"}`}>
                          {tk.status}
                        </span>
                        <span className="text-[10px] uppercase tracking-widest text-orange-400 font-bold">
                          {t(`support_cat_${tk.category}`) || tk.category}
                        </span>
                        <span className="text-xs text-zinc-500">
                          {tk.created_at ? new Date(tk.created_at).toLocaleString(lang) : "—"}
                        </span>
                      </div>
                      <div className="font-bold truncate">{tk.subject}</div>
                      <div className="text-xs text-zinc-400 truncate">{tk.name} · {tk.email}</div>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 pl-14 space-y-4" data-testid={`ticket-body-${tk.ticket_id}`}>
                      <div className="text-sm text-zinc-200 whitespace-pre-wrap bg-[#0E0E12]/30 border border-white/5 rounded-md p-4 leading-relaxed">
                        {tk.message}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <a
                          href={`mailto:${tk.email}?subject=Re:%20${encodeURIComponent(tk.subject)}`}
                          data-testid={`ticket-reply-${tk.ticket_id}`}
                          className="inline-flex items-center gap-2 rounded-full bg-orange-500 hover:bg-orange-600 text-black font-bold text-xs uppercase tracking-widest px-4 h-9"
                        >
                          <Mail className="w-3.5 h-3.5" /> {t("admin_ticket_reply")}
                        </a>
                        {isClosed ? (
                          <button
                            onClick={() => updateTicketStatus(tk.ticket_id, "open")}
                            data-testid={`ticket-reopen-${tk.ticket_id}`}
                            className="rounded-full bg-white/10 hover:bg-white/15 border border-white/10 text-white font-semibold text-xs uppercase tracking-widest px-4 h-9"
                          >
                            {t("admin_ticket_reopen")}
                          </button>
                        ) : (
                          <button
                            onClick={() => updateTicketStatus(tk.ticket_id, "closed")}
                            data-testid={`ticket-close-${tk.ticket_id}`}
                            className="rounded-full bg-white/10 hover:bg-white/15 border border-white/10 text-white font-semibold text-xs uppercase tracking-widest px-4 h-9"
                          >
                            {t("admin_ticket_close")}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Bulk delete test users */}
      <Dialog open={bulkOpen} onOpenChange={(o) => { if (!o && !bulkRunning) { setBulkOpen(false); setBulkConfirm(""); } }}>
        <DialogContent className="max-w-lg bg-zinc-950 border-white/10 text-white" data-testid="admin-bulk-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-400 flex items-center gap-2">
              <Trash2 className="w-5 h-5" /> {t("admin_bulk_title") || "Delete all test users"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="rounded-md border border-rose-500/30 bg-rose-500/[0.06] p-4">
              <p className="text-zinc-200 mb-3">
                {(t("admin_bulk_body") || "You are about to delete {n} accounts flagged as test (matching @example.com, @vxftest.example.com, or known test prefixes). All their prompts, credit lots, tickets and sessions will be cascaded.").replace("{n}", deletableTestUsers.length)}
              </p>
              <div className="text-[10px] uppercase tracking-widest text-rose-400 font-bold mb-2">{t("admin_bulk_matched") || "Matched accounts"}</div>
              <ul className="text-xs text-zinc-400 max-h-40 overflow-y-auto space-y-1 pr-2">
                {deletableTestUsers.map((u) => <li key={u.user_id} className="font-mono">{u.email}</li>)}
              </ul>
            </div>
            <div>
              <div className="text-xs text-zinc-400 mb-2">
                {t("admin_bulk_confirm_hint") || "To confirm, type exactly:"}{" "}
                <span className="font-mono text-orange-400">DELETE {deletableTestUsers.length}</span>
              </div>
              <Input
                autoFocus
                value={bulkConfirm}
                onChange={(e) => setBulkConfirm(e.target.value)}
                placeholder={`DELETE ${deletableTestUsers.length}`}
                data-testid="admin-bulk-confirm-input"
                disabled={bulkRunning}
                className="bg-[#0E0E12]/40 border-white/20 text-white font-mono text-sm"
              />
            </div>
            {bulkRunning && (
              <div className="text-xs text-zinc-400">
                {t("admin_bulk_progress") || "Deleting"}… {bulkProgress.done}/{bulkProgress.total}
                {bulkProgress.errors > 0 && <span className="text-rose-400"> · {bulkProgress.errors} errors</span>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setBulkOpen(false); setBulkConfirm(""); }} disabled={bulkRunning} data-testid="admin-bulk-cancel" className="text-zinc-300">
              {t("cancel") || "Cancel"}
            </Button>
            <Button
              onClick={runBulkDelete}
              disabled={bulkRunning || bulkConfirm.trim() !== `DELETE ${deletableTestUsers.length}` || deletableTestUsers.length === 0}
              data-testid="admin-bulk-confirm"
              className="bg-rose-500 hover:bg-rose-600 text-white font-bold"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {bulkRunning ? (t("admin_bulk_running") || "Deleting…") : (t("admin_bulk_run") || `Delete ${deletableTestUsers.length}`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete user confirmation */}
      <Dialog open={!!userToDelete} onOpenChange={(o) => { if (!o) closeDelete(); }}>
        <DialogContent className="max-w-lg bg-zinc-950 border-white/10 text-white" data-testid="admin-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-400 flex items-center gap-2">
              <Trash2 className="w-5 h-5" /> {t("admin_delete_user_title") || "Delete user permanently"}
            </DialogTitle>
          </DialogHeader>
          {userToDelete && (
            <div className="space-y-4 text-sm">
              <div className="rounded-md border border-rose-500/30 bg-rose-500/[0.06] p-4">
                <div className="text-[10px] uppercase tracking-widest text-rose-400 font-bold mb-2">{t("admin_delete_user_warning_title") || "This cannot be undone"}</div>
                <p className="text-zinc-300 mb-2">
                  {t("admin_delete_user_warning") || "The user and ALL related data will be permanently deleted:"}
                </p>
                <ul className="list-disc list-inside text-xs text-zinc-400 space-y-1">
                  <li>{t("admin_delete_item_prompts") || "Saved image/video prompts and custom lists"}</li>
                  <li>{t("admin_delete_item_text_prompts") || "AI Prompt Assistant library entries"}</li>
                  <li>{t("admin_delete_item_credits") || "Purchased credit lots and payment history"}</li>
                  <li>{t("admin_delete_item_tickets") || "Support tickets and sessions"}</li>
                  <li>{t("admin_delete_item_refs") || "Reference images (marked as deleted in DB)"}</li>
                </ul>
              </div>
              <div className="rounded-md border border-amber-500/30 bg-amber-500/[0.05] p-3 text-xs text-amber-100">
                <strong className="uppercase tracking-widest text-[10px] block mb-1 text-amber-400">Stripe</strong>
                {t("admin_delete_stripe_note") || "Any active Stripe subscription for this user will remain live in Stripe. Cancel it manually from the Stripe dashboard if needed."}
              </div>
              <div>
                <div className="text-xs text-zinc-400 mb-2">
                  {t("admin_delete_confirm_hint") || "To confirm, type the user email exactly:"}{" "}
                  <span className="font-mono text-orange-400">{userToDelete.email}</span>
                </div>
                <Input
                  autoFocus
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={userToDelete.email}
                  data-testid="admin-delete-confirm-input"
                  className="bg-[#0E0E12]/40 border-white/20 text-white font-mono text-sm"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={closeDelete} data-testid="admin-delete-cancel" className="text-zinc-300">
              {t("cancel") || "Cancel"}
            </Button>
            <Button
              onClick={doDelete}
              disabled={deleting || !userToDelete || confirmText.trim().toLowerCase() !== (userToDelete?.email || "").toLowerCase()}
              data-testid="admin-delete-confirm"
              className="bg-rose-500 hover:bg-rose-600 text-white font-bold"
            >
              <Trash2 className="w-4 h-4 mr-2" /> {deleting ? (t("admin_delete_deleting") || "Deleting…") : (t("admin_delete_user") || "Delete user")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
